import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import { useAuth } from '../contexts/AuthContext';
import { QrCode, Scan, X, Truck, User, MapPin, CheckCircle, AlertCircle, Droplet, PanelTop, Camera, ArrowRight, ArrowLeft } from 'lucide-react';
import jsQR from 'jsqr';
import { createAuditoriaCalidad, uploadAuditoriaImage, api } from '../api';

const AuditoriasCalidad = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { userPermissions } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeItem, setActiveItem] = useState('Auditorías de Calidad');
  const navigate = useNavigate();
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [scannedResult, setScannedResult] = useState('');
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    camion: '',
    operador: '',
    tractor: '',
    remolque: '',
    lt: '',
    destino: ''
  });
  
  // Estados para el proceso de auditoría manual por etapas
  const [modoAuditoria, setModoAuditoria] = useState(false);
  const [etapaAuditoria, setEtapaAuditoria] = useState(1);
  const [formAuditoria, setFormAuditoria] = useState({
    // Sección Fauna Nociva
    faunaNociva: false,
    fotosFaunaNociva: [],
    
    // Sección Filtraciones
    filtracionesLuzAgua: false,
    fotosFiltraciones: [],
    
    // Sección Estado Físico
    estadoFisico: 'Bueno', // 'Bueno', 'Regular', 'Malo'
    fotosEstadoFisico: [],
    
    // Sección Comentarios y Decisión Final
    comentarios: '',
    autorizado: null // true = autorizado, false = rechazado, null = no decidido
  });
  
  const handleLogout = () => {
    console.log('Logout clicked!');
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    setTimeout(() => {
      navigate('/login');
    }, 0);
  };

  // Función para abrir la cámara
  const startScanner = async () => {
    setIsScannerOpen(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: "environment" } // Usar cámara trasera si está disponible
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
      }
    } catch (err) {
      console.error("Error al acceder a la cámara:", err);
      alert("No se pudo acceder a la cámara. Por favor, verifica los permisos.");
      setIsScannerOpen(false);
    }
  };

  // Función para cerrar la cámara
  const stopScanner = () => {
    if (streamRef.current) {
      const tracks = streamRef.current.getTracks();
      tracks.forEach(track => track.stop());
      streamRef.current = null;
    }
    setIsScannerOpen(false);
  };  // Procesar el resultado del escaneo
  const handleScan = (data) => {
    if (data) {
      setScannedResult(data);
      stopScanner(); // Cerrar la cámara después de un escaneo exitoso
      
      // Extraer el código de referencia del embarque del QR
      // Asumimos que el QR contiene directamente la referencia, pero podría tener un formato específico
      let embarqueRef = data.trim();
      let referenceFound = false;
      
      console.log("Contenido del QR escaneado:", embarqueRef);
      
      // Verificar si la referencia tiene un formato válido para embarques
      // Ejemplo: Si todos los embarques comienzan con "AL" seguido de números
      if (embarqueRef.startsWith('AL') && embarqueRef.match(/^AL\d+$/)) {
        console.log("QR escaneado válido (formato AL + números):", embarqueRef);
        referenceFound = true;
      } else if (embarqueRef.match(/^[A-Za-z0-9]+$/)) {
        console.log("QR escaneado con formato alfanumérico básico:", embarqueRef);
        referenceFound = true;
      } else {
        // Si no tiene el formato esperado, intentar extraer el valor relevante
        try {
          // Verificar si es un JSON y extraer la referencia
          const parsedData = JSON.parse(embarqueRef);
          if (parsedData && parsedData.reference) {
            embarqueRef = parsedData.reference;
            console.log("Referencia extraída de JSON (campo reference):", embarqueRef);
            referenceFound = true;
          } else if (parsedData && parsedData.numeroReferencia) {
            embarqueRef = parsedData.numeroReferencia;
            console.log("Referencia extraída de JSON (campo numeroReferencia):", embarqueRef);
            referenceFound = true;
          }
        } catch (err) {
          // No es JSON, intentar buscar patrones en el texto
          const match = embarqueRef.match(/[A-Z]{2}\d+/); // Buscar patrón como AL12345
          if (match) {
            embarqueRef = match[0];
            console.log("Patrón de referencia encontrado en el texto:", embarqueRef);
            referenceFound = true;
          }
        }
      }
      
      if (!referenceFound) {
        console.warn("No se pudo identificar una referencia válida en el QR");
        alert("El código QR escaneado no contiene una referencia de embarque válida. Intente de nuevo.");
        setScannedResult('');
        return;
      }
      
      console.log("Redirigiendo con referencia:", embarqueRef);
      setTimeout(() => {
        // Redirigir a la página de confirmación de embarque con el código QR escaneado
        navigate(`/confirmar-embarque?qr=${encodeURIComponent(embarqueRef)}`);
      }, 1000); // Pequeña demora para mostrar el resultado antes de navegar
    }
  };
  
  // Función para analizar frames de video buscando códigos QR
  const scanQRFromVideo = () => {
    if (videoRef.current && streamRef.current) {
      const video = videoRef.current;
      
      // Crear un canvas para analizar la imagen
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      
      // Función que se ejecuta repetidamente para analizar frames
      const checkForQR = () => {
        if (!videoRef.current || !streamRef.current) return;
        
        // Ajustar el tamaño del canvas al tamaño del video
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        
        // Dibujar el frame actual en el canvas
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        // Obtener los datos de la imagen
        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
        
        // Analizar con jsQR
        const code = jsQR(imageData.data, imageData.width, imageData.height);
        
        if (code) {
          // Si se detecta un código QR
          handleScan(code.data);
          return; // Detener el escaneo
        }
        
        // Si no se detecta, seguir escaneando
        if (streamRef.current) {
          requestAnimationFrame(checkForQR);
        }
      };
      
      // Iniciar el proceso de escaneo
      requestAnimationFrame(checkForQR);
    }
  };
  // Limpiar al desmontar el componente
  useEffect(() => {
    return () => {
      // Asegurarse de detener la cámara cuando el componente se desmonte
      if (streamRef.current) {
        const tracks = streamRef.current.getTracks();
        tracks.forEach(track => track.stop());
      }
    };
  }, []);
  // Manejar cambios en el formulario
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  // Manejar cambios en el formulario de auditoría
  const handleAuditoriaFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormAuditoria({
      ...formAuditoria,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Función para iniciar el proceso de auditoría
  const iniciarAuditoria = () => {
    setModoAuditoria(true);
    setEtapaAuditoria(1); // Iniciar en la primera etapa
    setShowModal(false); // Cerrar el modal de datos iniciales
  };

  // Función para avanzar a la siguiente etapa de la auditoría
  const avanzarEtapa = () => {
    setEtapaAuditoria(prevEtapa => prevEtapa + 1);
  };

  // Función para retroceder a la etapa anterior
  const retrocederEtapa = () => {
    setEtapaAuditoria(prevEtapa => Math.max(1, prevEtapa - 1));
  };

  // Función para cancelar la auditoría
  const cancelarAuditoria = () => {
    if (window.confirm("¿Estás seguro de que deseas cancelar esta auditoría? Los datos no se guardarán.")) {
      setModoAuditoria(false);
      setEtapaAuditoria(1);
      // Reiniciar el formulario de auditoría
      setFormAuditoria({
        faunaNociva: false,
        fotosFaunaNociva: [],
        filtracionesLuzAgua: false,
        fotosFiltraciones: [],
        estadoFisico: 'Bueno',
        fotosEstadoFisico: [],
        comentarios: '',
        autorizado: null
      });
    }
  };

  // Función para manejar la subida de imágenes
  const handleImageUpload = (e, seccion) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    // Determinamos qué array de fotos actualizar y el tipo de evidencia según la sección
    let fotosKey;
    let tipoEvidencia;
    switch(seccion) {
      case 'faunaNociva':
        fotosKey = 'fotosFaunaNociva';
        tipoEvidencia = 'fauna';
        break;
      case 'filtraciones':
        fotosKey = 'fotosFiltraciones';
        tipoEvidencia = 'filtracion';
        break;
      case 'estadoFisico':
        fotosKey = 'fotosEstadoFisico';
        tipoEvidencia = 'estado';
        break;
      default:
        console.error('Sección de fotos no válida');
        return;
    }
    
    // Almacenamos las imágenes localmente
    const localFiles = files.map(f => ({
      nombre: f.name,
      url: URL.createObjectURL(f),
      file: f,
      tipoEvidencia: tipoEvidencia,
      pendienteSubida: true
    }));
    
    setFormAuditoria(prev => ({
      ...prev,
      [fotosKey]: [...prev[fotosKey], ...localFiles]
    }));
    
    console.log(`${files.length} imágenes agregadas a la sección ${seccion}`);
  };

  // Función para eliminar una foto de evidencia
  const eliminarFoto = (seccion, index) => {
    // Determinamos qué array de fotos actualizar según la sección
    let fotosKey;
    switch(seccion) {
      case 'faunaNociva':
        fotosKey = 'fotosFaunaNociva';
        break;
      case 'filtraciones':
        fotosKey = 'fotosFiltraciones';
        break;
      case 'estadoFisico':
        fotosKey = 'fotosEstadoFisico';
        break;
      default:
        console.error('Sección de fotos no válida');
        return;
    }
    
    const nuevosFotos = [...formAuditoria[fotosKey]];
    // Liberar la URL del objeto si fue creada localmente
    if (nuevosFotos[index].url && nuevosFotos[index].pendienteSubida) {
      URL.revokeObjectURL(nuevosFotos[index].url);
    }
    nuevosFotos.splice(index, 1);
    
    setFormAuditoria({
      ...formAuditoria,
      [fotosKey]: nuevosFotos
    });
  };
  // Función para enviar la auditoría completa
  const enviarAuditoria = async () => {
    setLoading(true);
    try {
      // Verificar que se haya tomado una decisión (autorizar o rechazar)
      if (formAuditoria.autorizado === null) {
        throw new Error('Debe seleccionar si autoriza o rechaza el embarque');
      }
      
      // Preparar los datos de la auditoría para enviar
      const auditoriaData = {
        // Información del embarque manual
        camion: formData.camion,
        operador: formData.operador,
        tractor: formData.tractor,
        remolque: formData.remolque,
        lt: formData.lt,
        destino: formData.destino,
        
        // Aspectos evaluados - inicialmente sin imágenes
        fauna_nociva: formAuditoria.faunaNociva,
        fauna_img: [],
        
        filtracion: formAuditoria.filtracionesLuzAgua,
        filtracion_img: [],
        
        estado_fisico: formAuditoria.estadoFisico.toUpperCase(),
        estado_img: [],
        
        // Decisión y comentarios
        comentarios: formAuditoria.comentarios,
        estatus: formAuditoria.autorizado ? 'AUTORIZADO' : 'RECHAZADO'
      };
      
      console.log('Enviando datos de auditoría manual:', auditoriaData);
      
      // 1. PRIMERO - Enviar los datos básicos de la auditoría
      const auditoriaResponse = await createAuditoriaCalidad(auditoriaData);
      
      if (!auditoriaResponse || !auditoriaResponse.id) {
        throw new Error('Error al crear la auditoría: No se recibió ID');
      }
      
      const auditoriaId = auditoriaResponse.id;
      console.log(`Auditoría creada con ID: ${auditoriaId}`);
      
      // 2. SEGUNDO - Ahora subir todas las imágenes a la auditoría ya creada
      // Verificamos si hay fotos pendientes de subir
      const pendingPhotos = [
        ...formAuditoria.fotosFaunaNociva.filter(f => f.file && f.pendienteSubida),
        ...formAuditoria.fotosFiltraciones.filter(f => f.file && f.pendienteSubida),
        ...formAuditoria.fotosEstadoFisico.filter(f => f.file && f.pendienteSubida)
      ];
      
      if (pendingPhotos.length > 0) {
        console.log(`Subiendo ${pendingPhotos.length} imágenes pendientes...`);
        try {
          // Subimos las fotos en grupos por su tipo y capturamos los IDs devueltos
          const faunaIds = await subirImagenesPorCategoria(auditoriaId, formAuditoria.fotosFaunaNociva, 'fauna');
          const filtracionIds = await subirImagenesPorCategoria(auditoriaId, formAuditoria.fotosFiltraciones, 'filtracion');
          const estadoIds = await subirImagenesPorCategoria(auditoriaId, formAuditoria.fotosEstadoFisico, 'estado');
          
          console.log("Todas las imágenes subidas correctamente");
        } catch (error) {
          console.error("Error al subir las imágenes:", error);
          alert(`Error al subir algunas imágenes: ${error.message}`);
          // Continuamos con el proceso aunque haya errores en algunas imágenes
        }
      }
      
      // Mostrar mensaje de éxito
      console.log('Auditoría registrada exitosamente con ID:', auditoriaId);
      
      // Mostrar notificación de éxito
      alert(`Auditoría ${auditoriaData.estatus === 'AUTORIZADO' ? 'autorizada' : 'rechazada'} correctamente`);
      
      // Redireccionar
      setModoAuditoria(false);
      navigate('/auditorias-calidad');
      
    } catch (error) {
      console.error('Error al enviar auditoría:', error);
      alert(`Error: ${error.message || 'Ocurrió un problema al enviar la auditoría'}`);
      setLoading(false);
    }
  };

  // Función auxiliar para subir imágenes por categoría
  const subirImagenesPorCategoria = async (auditoriaId, imagenes, tipoEvidencia) => {
    const imagenesPendientes = imagenes.filter(img => img.file && img.pendienteSubida);
    
    if (imagenesPendientes.length === 0) return;
    
    console.log(`Subiendo ${imagenesPendientes.length} imágenes de tipo ${tipoEvidencia}...`);
    
    // Creamos un FormData con todas las imágenes de la misma categoría
    const formData = new FormData();
    imagenesPendientes.forEach(img => {
      formData.append('files', img.file);
    });
    
    try {
      // Construimos el endpoint
      let endpoint = `/api/v1/auditorias/calidad/${auditoriaId}/upload_evidence`;
      if (tipoEvidencia) {
        endpoint += `?tipo_evidencia=${tipoEvidencia}`;
      }
      
      // Hacemos una única llamada para subir todas las imágenes de la misma categoría
      const response = await api.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });
      
      console.log(`Imágenes de tipo ${tipoEvidencia} subidas correctamente:`, response.data);
      
      // Retornar solo los IDs de las imágenes subidas
      if (response.data && response.data.files) {
        return response.data.files.map(file => file.id);
      }
      
      return response.data;
    } catch (error) {
      console.error(`Error al subir imágenes de tipo ${tipoEvidencia}:`, error);
      throw error;
    }
  };

  // Función para enviar el formulario inicial
  const handleFormSubmit = (e) => {
    e.preventDefault();
    console.log("Datos del formulario inicial:", formData);
    
    // Validar que todos los campos obligatorios estén completos
    const camposRequeridos = ['camion', 'operador', 'tractor', 'remolque', 'lt', 'destino'];
    const camposFaltantes = camposRequeridos.filter(campo => !formData[campo]);
    
    if (camposFaltantes.length > 0) {
      alert(`Por favor complete los siguientes campos: ${camposFaltantes.join(', ')}`);
      return;
    }
    
    // Iniciar el proceso de auditoría
    iniciarAuditoria();
  };

  useEffect(() => {
    document.title = "Auditorías de Calidad | Amerisa";
    
    // Agregar cualquier efecto adicional que sea necesario para inicializar la página
    const init = async () => {
      setLoading(true);
      try {
        // Aquí puedes realizar llamadas a API o inicialización de datos
        // Por ejemplo, cargar auditorías existentes
      } catch (error) {
        console.error("Error al cargar datos de auditorías:", error);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden">      
      {/* Sidebar condicional - en móviles solo mostrar cuando está abierto */}
      <div className={`${!sidebarOpen && 'hidden md:block'} transition-all duration-300 z-40 fixed md:relative`}>
        <Sidebar
          isOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          activeItem={activeItem}
          setActiveItem={setActiveItem}
          onLogout={handleLogout}
        />
      </div>
        
      {/* Contenido principal - ajustar márgenes para móviles */}
      <div className={`transition-all flex-1 ${sidebarOpen ? 'md:ml-64' : 'ml-0 md:ml-16'} bg-gray-100 min-h-screen w-full`}>
        <Topbar
          title="Auditorías de Calidad"
        />
          <main className="p-2 sm:p-4 md:p-6">
          <div className="bg-white p-3 sm:p-4 md:p-6 rounded-lg shadow mb-4 md:mb-6">
            <div className="flex justify-between items-center">
              <h1 className="text-xl md:text-2xl font-semibold text-gray-800 mb-3 md:mb-4">Auditorías de Calidad</h1>
              
              {/* Botón para mostrar/ocultar sidebar en móviles */}
              <button 
                className="md:hidden bg-sky-600 text-white p-2 rounded-md"
                onClick={() => setSidebarOpen(!sidebarOpen)}
              >
                <span className="sr-only">Menú</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" 
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {sidebarOpen ? <><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></> : 
                  <><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></>}
                </svg>
              </button>
            </div>
            
            {loading ? (
              <div className="flex justify-center items-center py-6 md:py-10">
                <div className="loader"></div>
                <p className="ml-2">Cargando datos...</p>
              </div>
            ) : (
              <div className="space-y-4 md:space-y-6">
                {/* Sección de escaneo de QR - optimizada para móvil */}
                <div className="flex flex-col items-center justify-center py-6 sm:py-8 px-2 sm:px-4 bg-sky-50 rounded-lg border-2 border-dashed border-sky-300 mb-4 md:mb-8">
                  <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-sky-800 mb-4 sm:mb-6">Escanea el código QR para iniciar</h2>
                  
                  {isScannerOpen ? (
                    <div className="relative w-full max-w-md">
                      <div className="bg-black rounded-lg overflow-hidden shadow-xl">                        <video 
                          ref={videoRef}
                          autoPlay
                          playsInline
                          className="w-full h-auto"
                          style={{ maxHeight: '70vh' }}
                          onLoadedMetadata={() => {
                            // Iniciar el escaneo de códigos QR cuando el video esté listo
                            scanQRFromVideo();
                          }}
                        />
                        <div className="absolute top-2 right-2">
                          <button 
                            className="bg-white p-2 rounded-full shadow-md" 
                            onClick={stopScanner}
                          >
                            <X size={24} className="text-red-600" />
                          </button>
                        </div>
                      </div>
                      <p className="text-center mt-4 text-gray-600">
                        Enfoca el código QR dentro del recuadro
                      </p>
                    </div>
                  ) : (                    <>
                      <div className="mb-4 sm:mb-6">
                        <QrCode 
                          size={window.innerWidth < 640 ? 120 : window.innerWidth < 768 ? 150 : 180} 
                          strokeWidth={1} 
                          className="text-sky-700" 
                        />
                      </div>                      <div 
                        className="bg-sky-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg flex items-center cursor-pointer hover:bg-sky-700 transition-colors"
                        onClick={startScanner}
                      >
                        <Scan 
                          size={window.innerWidth < 640 ? 24 : 28} 
                          className="mr-2" 
                        />
                        <span className="text-sm sm:text-lg font-medium">Iniciar escaneo</span>
                      </div>                      <div className="mt-4 text-center">
                        <div className="mb-2">
                          <span className="text-gray-600">- O -</span>
                        </div>
                        <div className="flex flex-col items-center">
                          <button 
                            className="text-sky-600 font-medium underline cursor-pointer hover:text-sky-800 mb-2"
                            onClick={() => navigate('/confirmar-embarque')}
                          >
                            Ir a búsqueda manual
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                  
                  {scannedResult && (
                    <div className="mt-4 p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg w-full">
                      <h3 className="font-medium text-green-800">Resultado escaneado:</h3>
                      <p className="text-green-700">{scannedResult}</p>
                    </div>                  )}                </div>               
                 <div className="flex flex-col items-center mt-4">
                   <span className="mb-3">Embarque no colocado por Amerisa?</span>
                  
                   <div 
                     className="bg-sky-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg flex items-center cursor-pointer hover:bg-sky-700 transition-colors"
                     onClick={() => setShowModal(true)}
                   >
                     <Truck 
                       size={window.innerWidth < 640 ? 24 : 28} 
                       className="mr-2" 
                     />
                     <span className="text-sm sm:text-lg font-medium">Auditoria manual</span>
                   </div>
                 </div> 
                
                
              </div>
            )}
          </div>
        </main>
      </div>      {/* Modal para datos iniciales de auditoría manual */}
      {showModal && !modoAuditoria && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-90vh overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-800">Auditoría Manual</h3>
                <button 
                  onClick={() => setShowModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={24} />
                </button>
              </div>
              
              <p className="text-gray-600 mb-4">Ingrese los datos del embarque para comenzar la auditoría manual:</p>
              
              <form onSubmit={handleFormSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Camión
                  </label>
                  <input
                    type="text"
                    name="camion"
                    value={formData.camion}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Operador
                  </label>
                  <input
                    type="text"
                    name="operador"
                    value={formData.operador}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tractor
                  </label>
                  <input
                    type="text"
                    name="tractor"
                    value={formData.tractor}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Remolque
                  </label>
                  <input
                    type="text"
                    name="remolque"
                    value={formData.remolque}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    LT
                  </label>
                  <input
                    type="text"
                    name="lt"
                    value={formData.lt}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Destino
                  </label>
                  <input
                    type="text"
                    name="destino"
                    value={formData.destino}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                    required
                  />
                </div>
                
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-sky-600 text-white rounded-md hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500"
                  >
                    Continuar con la Auditoría
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de proceso de auditoría por etapas */}
      {modoAuditoria && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-70 z-50 flex flex-col">
          <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full mx-auto my-auto overflow-y-auto max-h-[90vh] flex flex-col">
            {/* Header del modal */}
            <div className="sticky top-0 bg-sky-600 text-white px-4 py-3 flex justify-between items-center rounded-t-lg">
              <h2 className="font-semibold text-lg">
                Auditoría Manual - Etapa {etapaAuditoria} de 4
              </h2>
              <button 
                onClick={cancelarAuditoria}
                className="p-1 hover:bg-sky-700 rounded-full focus:outline-none"
              >
                <X size={24} />
              </button>
            </div>
            
            {/* Barra de progreso */}
            <div className="px-4 pt-3">
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-sky-600 transition-all duration-300 ease-in-out rounded-full"
                  style={{ width: `${(etapaAuditoria / 4) * 100}%` }}
                ></div>
              </div>
              <div className="flex justify-between text-xs mt-1 text-gray-500">
                <span>Fauna</span>
                <span>Filtraciones</span>
                <span>Estado Físico</span>
                <span>Revisión</span>
              </div>
            </div>

            {/* Contenido de la etapa actual */}
            <div className="flex-1 p-4 overflow-y-auto">
              {/* Etapa 1: Fauna Nociva */}
              {etapaAuditoria === 1 && (
                <div className="space-y-4">
                  <h3 className="text-xl font-medium flex items-center">
                    <AlertCircle className="mr-2 text-amber-500" size={24} /> 
                    Evaluación de Fauna Nociva
                  </h3>
                  
                  <p className="text-gray-600">
                    Verifique si existe presencia de fauna nociva (insectos, roedores, etc.) en el vehículo o embarque.
                  </p>

                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="font-medium mb-2">¿Se detectó fauna nociva?</div>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="radio"
                          name="faunaNociva"
                          checked={formAuditoria.faunaNociva === true}
                          onChange={() => setFormAuditoria({...formAuditoria, faunaNociva: true})}
                          className="h-5 w-5 text-sky-600"
                        />
                        <span className="ml-2">Sí, se detectó</span>
                      </label>
                      
                      <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="radio"
                          name="faunaNociva"
                          checked={formAuditoria.faunaNociva === false}
                          onChange={() => setFormAuditoria({...formAuditoria, faunaNociva: false})}
                          className="h-5 w-5 text-sky-600"
                        />
                        <span className="ml-2">No, está libre de fauna</span>
                      </label>
                    </div>
                  </div>

                  {/* Mostrar sección de fotos solo si se detectó fauna */}
                  {formAuditoria.faunaNociva && (
                    <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                      <h4 className="font-medium mb-2 flex items-center">
                        <Camera size={18} className="mr-2 text-sky-600" />
                        Evidencia Fotográfica
                      </h4>
                      
                      <p className="text-sm text-gray-600 mb-3">
                        Tome fotos que muestren claramente la fauna nociva detectada
                      </p>
                      
                      {/* Sección para subir fotos */}
                      <div className="mb-2">
                        <label 
                          htmlFor="fotosFaunaNociva" 
                          className="flex items-center justify-center p-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50"
                        >
                          <Camera size={20} className="mr-2 text-sky-600" />
                          <span>Tomar foto de evidencia</span>
                          <input
                            type="file"
                            id="fotosFaunaNociva"
                            accept="image/*"
                            capture="environment"
                            multiple
                            onChange={(e) => handleImageUpload(e, 'faunaNociva')}
                            className="sr-only"
                          />
                        </label>
                      </div>
                      
                      {/* Previsualización de fotos */}
                      {formAuditoria.fotosFaunaNociva.length > 0 && (
                        <div className="mt-3">
                          <h5 className="text-sm font-medium mb-2">Fotos capturadas ({formAuditoria.fotosFaunaNociva.length})</h5>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {formAuditoria.fotosFaunaNociva.map((foto, index) => (
                              <div key={index} className="relative">
                                <img 
                                  src={foto.url} 
                                  alt={`Evidencia ${index + 1}`} 
                                  className="w-full h-24 object-cover rounded-md"
                                />
                                <button
                                  onClick={() => eliminarFoto('faunaNociva', index)}
                                  className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1"
                                  title="Eliminar foto"
                                >
                                  <X size={14} />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {/* Etapa 2: Filtraciones */}
              {etapaAuditoria === 2 && (
                <div className="space-y-4">
                  <h3 className="text-xl font-medium flex items-center">
                    <Droplet className="mr-2 text-sky-500" size={24} /> 
                    Evaluación de Filtraciones
                  </h3>
                  
                  <p className="text-gray-600">
                    Verifique si existen filtraciones de luz o agua en el vehículo que podrían comprometer la carga.
                  </p>

                  <div className="p-3 bg-sky-50 border border-sky-200 rounded-lg">
                    <div className="font-medium mb-2">¿Se detectaron filtraciones?</div>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="radio"
                          name="filtracionesLuzAgua"
                          checked={formAuditoria.filtracionesLuzAgua === true}
                          onChange={() => setFormAuditoria({...formAuditoria, filtracionesLuzAgua: true})}
                          className="h-5 w-5 text-sky-600"
                        />
                        <span className="ml-2">Sí, hay filtraciones</span>
                      </label>
                      
                      <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="radio"
                          name="filtracionesLuzAgua"
                          checked={formAuditoria.filtracionesLuzAgua === false}
                          onChange={() => setFormAuditoria({...formAuditoria, filtracionesLuzAgua: false})}
                          className="h-5 w-5 text-sky-600"
                        />
                        <span className="ml-2">No, sin filtraciones</span>
                      </label>
                    </div>
                  </div>

                  {/* Mostrar sección de fotos solo si se detectaron filtraciones */}
                  {formAuditoria.filtracionesLuzAgua && (
                    <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                      <h4 className="font-medium mb-2 flex items-center">
                        <Camera size={18} className="mr-2 text-sky-600" />
                        Evidencia Fotográfica
                      </h4>
                      
                      <p className="text-sm text-gray-600 mb-3">
                        Tome fotos que muestren claramente las filtraciones detectadas
                      </p>
                      
                      {/* Sección para subir fotos */}
                      <div className="mb-2">
                        <label 
                          htmlFor="fotosFiltraciones" 
                          className="flex items-center justify-center p-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50"
                        >
                          <Camera size={20} className="mr-2 text-sky-600" />
                          <span>Tomar foto de evidencia</span>
                          <input
                            type="file"
                            id="fotosFiltraciones"
                            accept="image/*"
                            capture="environment"
                            multiple
                            onChange={(e) => handleImageUpload(e, 'filtraciones')}
                            className="sr-only"
                          />
                        </label>
                      </div>
                      
                      {/* Previsualización de fotos */}
                      {formAuditoria.fotosFiltraciones.length > 0 && (
                        <div className="mt-3">
                          <h5 className="text-sm font-medium mb-2">Fotos capturadas ({formAuditoria.fotosFiltraciones.length})</h5>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {formAuditoria.fotosFiltraciones.map((foto, index) => (
                              <div key={index} className="relative">
                                <img 
                                  src={foto.url} 
                                  alt={`Evidencia ${index + 1}`} 
                                  className="w-full h-24 object-cover rounded-md"
                                />
                                <button
                                  onClick={() => eliminarFoto('filtraciones', index)}
                                  className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1"
                                  title="Eliminar foto"
                                >
                                  <X size={14} />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {/* Etapa 3: Estado Físico */}
              {etapaAuditoria === 3 && (
                <div className="space-y-4">
                  <h3 className="text-xl font-medium flex items-center">
                    <PanelTop className="mr-2 text-indigo-500" size={24} /> 
                    Estado Físico
                  </h3>
                  
                  <p className="text-gray-600">
                    Evalúe el estado físico de paredes y piso del vehículo.
                  </p>

                  <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                    <div className="font-medium mb-2">¿Cómo calificaría el estado físico?</div>
                    <div className="flex flex-col gap-2">
                      <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="radio"
                          name="estadoFisico"
                          value="Bueno"
                          checked={formAuditoria.estadoFisico === 'Bueno'}
                          onChange={handleAuditoriaFormChange}
                          className="h-5 w-5 text-green-600"
                        />
                        <span className="ml-2 flex items-center">
                          <span className="h-3 w-3 rounded-full bg-green-500 inline-block mr-1"></span> 
                          Bueno - Sin problemas aparentes
                        </span>
                      </label>
                      
                      <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="radio"
                          name="estadoFisico"
                          value="Regular"
                          checked={formAuditoria.estadoFisico === 'Regular'}
                          onChange={handleAuditoriaFormChange}
                          className="h-5 w-5 text-amber-600"
                        />
                        <span className="ml-2 flex items-center">
                          <span className="h-3 w-3 rounded-full bg-amber-500 inline-block mr-1"></span> 
                          Regular - Algunos problemas menores
                        </span>
                      </label>
                      
                      <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="radio"
                          name="estadoFisico"
                          value="Malo"
                          checked={formAuditoria.estadoFisico === 'Malo'}
                          onChange={handleAuditoriaFormChange}
                          className="h-5 w-5 text-red-600"
                        />
                        <span className="ml-2 flex items-center">
                          <span className="h-3 w-3 rounded-full bg-red-500 inline-block mr-1"></span> 
                          Malo - Problemas importantes
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Sección de fotos */}
                  <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <h4 className="font-medium mb-2 flex items-center">
                      <Camera size={18} className="mr-2 text-sky-600" />
                      Evidencia Fotográfica
                    </h4>
                    
                    <p className="text-sm text-gray-600 mb-3">
                      Tome fotos que muestren el estado de paredes y piso
                    </p>
                    
                    {/* Sección para subir fotos */}
                    <div className="mb-2">
                      <label 
                        htmlFor="fotosEstadoFisico" 
                        className="flex items-center justify-center p-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50"
                      >
                        <Camera size={20} className="mr-2 text-sky-600" />
                        <span>Tomar foto de evidencia</span>
                        <input
                          type="file"
                          id="fotosEstadoFisico"
                          accept="image/*"
                          capture="environment"
                          multiple
                          onChange={(e) => handleImageUpload(e, 'estadoFisico')}
                          className="sr-only"
                        />
                      </label>
                    </div>
                    
                    {/* Previsualización de fotos */}
                    {formAuditoria.fotosEstadoFisico.length > 0 && (
                      <div className="mt-3">
                        <h5 className="text-sm font-medium mb-2">Fotos capturadas ({formAuditoria.fotosEstadoFisico.length})</h5>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {formAuditoria.fotosEstadoFisico.map((foto, index) => (
                            <div key={index} className="relative">
                              <img 
                                src={foto.url} 
                                alt={`Evidencia ${index + 1}`} 
                                className="w-full h-24 object-cover rounded-md"
                              />
                              <button
                                onClick={() => eliminarFoto('estadoFisico', index)}
                                className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1"
                                title="Eliminar foto"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Etapa 4: Comentarios y Decisión Final */}
              {etapaAuditoria === 4 && (
                <div className="space-y-4">
                  <h3 className="text-xl font-medium flex items-center">
                    <CheckCircle className="mr-2 text-green-500" size={24} /> 
                    Revisión y Decisión Final
                  </h3>
                  
                  <div className="p-4 border border-sky-100 rounded-lg bg-sky-50 mb-4">
                    <h4 className="text-md font-semibold text-sky-800 mb-2">Datos del Embarque</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="font-medium">Camión:</span> {formData.camion}
                      </div>
                      <div>
                        <span className="font-medium">Operador:</span> {formData.operador}
                      </div>
                      <div>
                        <span className="font-medium">Tractor:</span> {formData.tractor}
                      </div>
                      <div>
                        <span className="font-medium">Remolque:</span> {formData.remolque}
                      </div>
                      <div>
                        <span className="font-medium">LT:</span> {formData.lt}
                      </div>
                      <div>
                        <span className="font-medium">Destino:</span> {formData.destino}
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 mb-2">
                    Resumen de la evaluación:
                  </p>
                  
                  <div className="space-y-3 p-3 bg-gray-50 border border-gray-200 rounded-lg mb-4">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Fauna Nociva:</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        formAuditoria.faunaNociva 
                          ? 'bg-red-100 text-red-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {formAuditoria.faunaNociva ? 'Detectada' : 'No Detectada'}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Filtraciones:</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        formAuditoria.filtracionesLuzAgua 
                          ? 'bg-red-100 text-red-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {formAuditoria.filtracionesLuzAgua ? 'Detectadas' : 'No Detectadas'}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Estado Físico:</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        formAuditoria.estadoFisico === 'Bueno' ? 'bg-green-100 text-green-800' :
                        formAuditoria.estadoFisico === 'Regular' ? 'bg-amber-100 text-amber-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {formAuditoria.estadoFisico}
                      </span>
                    </div>
                    
                    <div className="pt-2 mt-2 border-t border-gray-200">
                      <span className="font-medium">Total de evidencias:</span>
                      <ul className="mt-1 pl-4 text-sm">
                        <li>Fauna nociva: {formAuditoria.fotosFaunaNociva.length} fotos</li>
                        <li>Filtraciones: {formAuditoria.fotosFiltraciones.length} fotos</li>
                        <li>Estado físico: {formAuditoria.fotosEstadoFisico.length} fotos</li>
                      </ul>
                    </div>
                  </div>
                  
                  {/* Comentarios */}
                  <div className="space-y-2">
                    <label htmlFor="comentarios" className="block font-medium text-gray-700">
                      Comentarios adicionales:
                    </label>
                    <textarea
                      id="comentarios"
                      name="comentarios"
                      rows="4"
                      value={formAuditoria.comentarios}
                      onChange={handleAuditoriaFormChange}
                      placeholder="Ingrese aquí cualquier observación o comentario adicional sobre la auditoría..."
                      className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                    ></textarea>
                  </div>
                  
                  {/* Decisión final */}
                  <div className="p-4 bg-gray-100 rounded-lg border border-gray-200">
                    <h4 className="font-medium mb-3">Decisión Final</h4>
                    
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        type="button"
                        onClick={() => setFormAuditoria({...formAuditoria, autorizado: true})}
                        className={`flex-1 py-3 px-4 rounded-lg flex items-center justify-center ${
                          formAuditoria.autorizado === true 
                            ? 'bg-green-600 text-white' 
                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <CheckCircle size={18} className="mr-2" />
                        Autorizar Embarque
                      </button>
                      
                      <button
                        type="button"
                        onClick={() => setFormAuditoria({...formAuditoria, autorizado: false})}
                        className={`flex-1 py-3 px-4 rounded-lg flex items-center justify-center ${
                          formAuditoria.autorizado === false 
                            ? 'bg-red-600 text-white' 
                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <X size={18} className="mr-2" />
                        Rechazar Embarque
                      </button>
                    </div>
                    
                    {formAuditoria.autorizado === false && (
                      <p className="text-sm text-red-500 mt-2">
                        <AlertCircle size={14} className="inline mr-1" />
                        El embarque será rechazado por no cumplir con los estándares de calidad.
                      </p>
                    )}
                    
                    {formAuditoria.autorizado === true && (
                      <p className="text-sm text-green-600 mt-2">
                        <CheckCircle size={14} className="inline mr-1" />
                        El embarque será autorizado para continuar su proceso.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* Footer con botones de navegación */}
            <div className="sticky bottom-0 bg-gray-100 px-4 py-3 rounded-b-lg border-t border-gray-200 flex justify-between">
              {etapaAuditoria > 1 ? (
                <button 
                  onClick={retrocederEtapa}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors focus:outline-none flex items-center"
                >
                  <ArrowLeft size={16} className="mr-1" />
                  Anterior
                </button>
              ) : (
                <button 
                  onClick={cancelarAuditoria}
                  className="px-4 py-2 border border-red-200 text-red-600 rounded-md hover:bg-red-50 transition-colors focus:outline-none"
                >
                  Cancelar
                </button>
              )}
              
              {etapaAuditoria < 4 ? (
                <button 
                  onClick={avanzarEtapa}
                  className="px-4 py-2 bg-sky-600 text-white rounded-md hover:bg-sky-700 transition-colors focus:outline-none flex items-center"
                >
                  Siguiente
                  <ArrowRight size={16} className="ml-1" />
                </button>
              ) : (
                <button 
                  onClick={enviarAuditoria}
                  disabled={formAuditoria.autorizado === null || loading}
                  className={`px-4 py-2 rounded-md transition-colors focus:outline-none ${
                    formAuditoria.autorizado === null || loading
                      ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {loading ? 'Enviando...' : 'Finalizar Auditoría'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditoriasCalidad;
